import type { TOC } from '@ember/component/template-only';
import {
  PrismButton,
  PrismCard,
  PrismComboSelectInput,
  PrismLoadingBar,
  PrismToggle,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import SettingsTransactionMappingFilterBar from 'lumen/components/settings/transaction-mapping/filter-bar';
import SettingsTransactionMappingTableMapping from 'lumen/components/settings/transaction-mapping/table-mapping';
import type MarketSettingsTransationMappingController from 'lumen/controllers/market/settings/transaction-mapping';
import type MarketSettingsTransationMappingRoute from 'lumen/routes/market/settings/transaction-mapping';
import type { ModelFrom } from 'lumen/utils/routes';

interface MarketSettingsTransationMappingSignature {
  Args: {
    controller: MarketSettingsTransationMappingController;
    model: ModelFrom<MarketSettingsTransationMappingRoute>;
  };
}

<template>
  {{#if @model.mapping.isRunning}}
    <PrismLoadingBar @isFixed={{true}} @positionTop={{60}} />
  {{/if}}

  <SettingsPageTemplate
    @description={{t "settings.transaction_mapping.description"}}
    @isFullHeight={{true}}
    @title={{t "settings.section.transaction_mapping"}}
  >
    <PrismCard @contentLayout="noPadding" class="h-full overflow-hidden">
      <:header as |Header|>
        <div class="flex items-center gap-200">
          <Header.title>{{t "general.mapping"}}</Header.title>
          {{#unless @model.mapping.isRunning}}
            <span class="text-200--regular">{{t
                "general.item.count"
                count=@controller.numberOfItems
              }}</span>
          {{/unless}}
        </div>
      </:header>
      <:headerAfter>
        <div class="flex gap-300">
          <div class="flex items-center gap-100">
            <SettingsTransactionMappingFilterBar
              @filters={{@controller.filters}}
              @onRemoveFilterClicked={{@controller.onRemoveFilterClicked}}
            />

            <PrismComboSelectInput
              @canUnselectAll={{true}}
              @isSearchable={{true}}
              @onChange={{@controller.setFilters}}
              @options={{@controller.filterOptions}}
              @selectAllLabel={{t "total_revenue.all_results"}}
              @unsafe_collapsibleGroupBackLabel={{t
                "general.back_to_categories"
              }}
              @unsafe_triggerComponent={{component
                GeneralDropdownsTriggersTriggerDropdownIconButton
                buttonType="secondary"
                iconBefore="plus"
                label=(t "general.filter")
              }}
              @value={{@controller.filters}}
              data-test-id="dropdown-filter"
            />
          </div>

          <PrismToggle
            @dataTestId="toggle-only-unmapped"
            @isChecked={{@controller.isOnlyUnmappedShown}}
            @label={{t "settings.transaction_mapping.only_unmapped"}}
            @onChange={{@controller.toggleOnlyUnmapped}}
          />
        </div>
      </:headerAfter>
      <:content>
        <SettingsTransactionMappingTableMapping
          @handleMapItems={{@controller.mapItems}}
          @hierarchyCategories={{@controller.hierarchyCategories}}
          @isLoading={{@model.mapping.isRunning}}
          @mappingItems={{@controller.filteredMappingItems}}
        />
      </:content>
      <:footer>
        <div class="flex justify-end gap-200">
          <PrismButton
            @buttonType="secondary"
            @isDisabled={{@controller.isFooterButtonsDisabled}}
            @label={{t "general.cancel"}}
            @onClick={{@controller.onCancel}}
            data-test-id="button-cancel"
          />
          <PrismButton
            @isDisabled={{@controller.isFooterButtonsDisabled}}
            @isLoading={{@controller.saveTask.isRunning}}
            @label={{t "general.save"}}
            @onClick={{@controller.saveTask.perform}}
            data-test-id="button-save"
          />
        </div>

      </:footer>
    </PrismCard>
  </SettingsPageTemplate>
</template> satisfies TOC<MarketSettingsTransationMappingSignature>;
